import { useParams, useNavigate } from 'react-router-dom';
import { useFreelancerInvoiceDetail } from '../../hooks/useFreelancerInvoices';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Spinner } from '../../components/common/Spinner';
import { DEDUCTED_FIELD_LABELS } from '../../services/api/employeeSalary';
import { formatDisplayDate } from '../../utils/format';
import { formatMonthYear } from '../../utils/dateFormat';
import { LEGACY_CZ_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

function formatCurrency(value: number): string {
  return formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE);
}

export default function FreelancerInvoiceDetailPage() {
  const { userId: uidParam, year: yearParam, month: monthParam } = useParams<{ userId: string; year: string; month: string }>();
  const navigate = useNavigate();
  const userId = uidParam ? parseInt(uidParam, 10) : 0;
  const year = yearParam ? parseInt(yearParam, 10) : 0;
  const month = monthParam ? parseInt(monthParam, 10) : 0;

  const { data: invoice, isLoading, error } = useFreelancerInvoiceDetail(userId, year, month);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error ? (error as Error).message : 'No invoice data for this period'}</p>
        <button onClick={() => navigate(-1)} className="mt-2 text-sm text-blue-600 hover:text-blue-800">
          &larr; Back
        </button>
      </div>
    );
  }

  // MKS-351 — "October 2026" from the shared util.
  const monthName = formatMonthYear(new Date(invoice.year, invoice.month - 1));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate(`/reports/freelancer-invoices`)} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
            &larr; Back to Freelancer Invoices
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{invoice.userName} — {monthName}</h1>
        </div>
        <StatusBadge state={invoice.state} size="lg" />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Total Hours</div>
          <div className="text-xl font-semibold text-gray-900">{invoice.totalHours.toFixed(1)}h</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Billing</div>
          <div className="text-xl font-semibold text-gray-900">{formatCurrency(invoice.totalBilling)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Expenses</div>
          <div className="text-xl font-semibold text-gray-900">{formatCurrency(invoice.totalExpenses)}</div>
        </div>
        {invoice.totalExtraCosts > 0 && (
          <div className="bg-white rounded-lg border border-red-200 p-4">
            <div className="text-sm text-gray-500">Extra Costs</div>
            <div className="text-xl font-semibold text-red-600">-{formatCurrency(invoice.totalExtraCosts)}</div>
          </div>
        )}
        {invoice.salaryAmount != null && invoice.salaryAmount > 0 && (
          <div className="bg-white rounded-lg border border-red-200 p-4">
            <div className="text-sm text-gray-500">
              {invoice.deductedField ? DEDUCTED_FIELD_LABELS[invoice.deductedField] : 'Salary'}
            </div>
            <div className="text-xl font-semibold text-red-600">-{formatCurrency(invoice.salaryAmount)}</div>
          </div>
        )}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Net Amount</div>
          <div className="text-xl font-semibold text-gray-900">{formatCurrency(invoice.netAmount)}</div>
        </div>
      </div>

      {/* By Project */}
      {invoice.byProject.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">By Project</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hours × Rate</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.byProject.map((project, idx) => (
                  <tr key={`${project.projectId}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.projectName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{project.hours.toFixed(1)}h × {formatCurrency(project.rate)} Kč</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(project.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{invoice.totalHours.toFixed(1)}h</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(invoice.totalBilling)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expenses */}
      {invoice.expenses.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Expenses</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.expenses.map((expense) => (
                  <tr key={expense.expenseSheetId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{expense.projectName || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{expense.description || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(expense.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900" colSpan={2}>Total</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(invoice.totalExpenses)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Extra Costs (Deductions) */}
      {invoice.extraCosts && invoice.extraCosts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Extra Costs (Deductions)</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Deducted</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.extraCosts.map((ec) => (
                  <tr key={ec.extraCostId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{ec.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDisplayDate(ec.costDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600 text-right font-medium">
                      -{formatCurrency(ec.appliedAmount)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900" colSpan={2}>Total Deducted</td>
                  <td className="px-4 py-3 text-sm text-red-600 text-right">
                    -{formatCurrency(invoice.totalExtraCosts)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {invoice.carryOver > 0 && (
            <div className="mt-3 rounded-lg bg-yellow-50 border border-yellow-200 p-3">
              <p className="text-sm text-yellow-800">
                {formatCurrency(invoice.carryOver)} CZK could not be deducted this month and will carry over to the next invoice.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Grand Total */}
      {/* Same derivation as the employee's own view, so the two never disagree. */}
      <div className="bg-white rounded-lg border-2 border-gray-900 p-4 space-y-2">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Billing</span>
          <span>{formatCurrency(invoice.totalBilling)} CZK</span>
        </div>
        {invoice.totalExtraCosts > 0 && (
          <div className="flex justify-between items-center text-sm text-red-600">
            <span>Extra costs</span>
            <span>-{formatCurrency(invoice.totalExtraCosts)} CZK</span>
          </div>
        )}
        {invoice.salaryAmount != null && invoice.salaryAmount > 0 && (
          <div className="flex justify-between items-center text-sm text-red-600">
            <span>
              {invoice.deductedField
                ? `${DEDUCTED_FIELD_LABELS[invoice.deductedField]} deduction`
                : 'Salary deduction'}
            </span>
            <span>-{formatCurrency(invoice.salaryAmount)} CZK</span>
          </div>
        )}
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Expenses</span>
          <span>+{formatCurrency(invoice.totalExpenses)} CZK</span>
        </div>
        <div className="flex justify-between items-center border-t border-gray-200 pt-2">
          <span className="text-lg font-semibold text-gray-900">To be invoiced</span>
          <span className="text-lg font-semibold text-gray-900">
            {formatCurrency(invoice.invoiceAfterSalary)} CZK without VAT
          </span>
        </div>
      </div>
    </div>
  );
}
